import React from 'react';
import { ActionIcon, Group, Text } from '@mantine/core';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import DurationChip from './DurationChip';
import classes from './sceneCard.module.css';

type SceneCardProps = {
    /** Scene name shown centered on the tile. */
    name: string;
    /** Play duration; when set, a duration chip shows bottom-left. */
    durationSeconds?: number;
    /** Makes the duration chip editable (playlist context). */
    onDurationChange?: (seconds: number) => void;
    /** Shows an edit control top-right, left of delete. */
    onEdit?: () => void;
    /** Shows a delete control top-right. */
    onDelete?: () => void;
    /** Makes the whole tile activate (open/edit the scene). */
    onClick?: () => void;
    /**
     * A rendered preview (e.g. a ScenePreviewFrame) that fills the tile behind the
     * name/duration/icons. When set, the name moves to a scrimmed top bar so it
     * stays legible over the preview instead of centered on a blank tile.
     */
    preview?: React.ReactNode;
};

/**
 * The portrait scene tile used across the studio — in a playlist's scene strip,
 * in the Add-scene picker, and in the mobile scene list. Composed to a fixed
 * screen aspect ratio; the surrounding layout sets its width.
 */
const SceneCard: React.FC<SceneCardProps> = ({
    name,
    durationSeconds,
    onDurationChange,
    onEdit,
    onDelete,
    onClick,
    preview,
}) => {
    const interactive = Boolean(onClick);

    return (
        <div
            className={interactive ? `${classes.card} ${classes.clickable}` : classes.card}
            onClick={onClick}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            onKeyDown={
                interactive
                    ? event => {
                          if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onClick?.();
                          }
                      }
                    : undefined
            }
        >
            {preview && <div className={classes.preview}>{preview}</div>}

            {preview ? (
                <div className={classes.topBar}>
                    <Text className={classes.topName} lineClamp={1}>
                        {name}
                    </Text>
                </div>
            ) : (
                <Text className={classes.name}>{name}</Text>
            )}

            {(onEdit || onDelete) && (
                <Group className={classes.actions} gap={6} wrap="nowrap">
                    {onEdit && (
                        <ActionIcon
                            variant="white"
                            color="dark"
                            radius="xl"
                            size="md"
                            aria-label={`Edit ${name}`}
                            onPointerDown={event => event.stopPropagation()}
                            onClick={event => {
                                event.stopPropagation();
                                onEdit();
                            }}
                        >
                            <IconPencil size={16} />
                        </ActionIcon>
                    )}
                    {onDelete && (
                        <ActionIcon
                            variant="white"
                            color="dark"
                            radius="xl"
                            size="md"
                            aria-label={`Remove ${name}`}
                            onPointerDown={event => event.stopPropagation()}
                            onClick={event => {
                                event.stopPropagation();
                                onDelete();
                            }}
                        >
                            <IconTrash size={16} />
                        </ActionIcon>
                    )}
                </Group>
            )}

            {durationSeconds !== undefined && (
                <div
                    className={classes.duration}
                    // Keep clicks/drags on the editable duration from opening the
                    // scene or starting a reorder drag.
                    onClick={event => event.stopPropagation()}
                    onPointerDown={event => event.stopPropagation()}
                >
                    <DurationChip seconds={durationSeconds} onCommit={onDurationChange} />
                </div>
            )}
        </div>
    );
};

export default SceneCard;
